import { REPUB_COUNTRY, type RequestContext } from '@cx-web/domain-types';
import { CATEGORY_ORDERING_CONFIGS, REPUB_CONFIG, VALUE_BETS_CONFIGS } from '@cx-web/repub-config';
import { getRepubMetaFromRepub, isOcPlusRepub } from '@cx-web/utils';
import { Progress } from '@fpsm/cxy-components/Progress';
import memoize from 'lodash.memoize';

import { REPUBS } from '@cx-modules/constants';

import { BetButton } from '~/apps/product-plus/components/bet-button/BetButton';
import { BetTypeLabel } from '~/apps/product-plus/components/BettingInsights/components/BetTypeLabels/BetTypeLabels';
import {
  CheckboxFilter,
  DateRefinement,
  SliderFilter,
} from '~/apps/product-plus/components/BettingInsights/components/Filters';
import { PositiveEv, styles } from '~/apps/product-plus/components/BettingInsights/components/PositiveValue';
import { Profile } from '~/apps/product-plus/components/BettingInsights/components/Profile';
import { Autocomplete } from '~/apps/product-plus/components/BettingInsights/components/Search';
import { trackEvBetClick } from '~/apps/product-plus/components/BettingInsights/tracking';
import { type MarketDisplayNames } from '~/apps/product-plus/components/BettingInsights/types';
import {
  type BettingInsightsConfigFactoryOptions,
  type PositiveEvIndex,
} from '~/apps/product-plus/configuration/types';
import {
  type BettingInsightsUIConfig,
  createBettingInsightsUIConfig,
} from '~/apps/product-plus/configuration/ui/BettingInsightsUI';
import { formatDate } from '~/apps/product-plus/configuration/utils/formatDate';
import { hasMinBookmakersForRepub } from '~/apps/product-plus/configuration/utils/hasMinBookmakersForRepub';
import { FILTERS_CONFIGS } from '~/apps/product-plus/search-domain/config/filters-config';
import { FOOTBALL } from '~/apps/product-plus/search-domain/constants/categoryGroupNames';
import {
  EVENT_DISPLAY_NAME,
  getEventDisplayNames,
} from '~/apps/product-plus/search-domain/constants/eventDisplayNames';
import { getTimezoneForRepub } from '~/apps/product-plus/search-domain/constants/timezones';
import { positiveEvIndex } from '~/apps/product-plus/search-domain/indexes';
import { featureFlags } from '~/providers/feature-flag-provider';
import { getMatchupTeamInfo } from '~/utils/getMatchupTeamInfo';

export function createPositiveEvUIConfig(
  requestContext: RequestContext,
  options: BettingInsightsConfigFactoryOptions = {},
  tags: string[],
) {
  const { geolocationInfo, usageContext = 'default' } = options;

  const translations = FILTERS_CONFIGS[requestContext.repubConfig.name as keyof typeof FILTERS_CONFIGS];
  const evTranslations = VALUE_BETS_CONFIGS[requestContext.repubConfig.name as keyof typeof VALUE_BETS_CONFIGS];

  const timezone = getTimezoneForRepub(requestContext.repubConfig.name);

  const dateAttribute = timezone ? 'subeventStartDateLocal' : 'subeventStartDate';

  const { repubCountry: factoryRepubCountry, repubIs: factoryRepubIs } = getRepubMetaFromRepub(
    requestContext.repubConfig.name,
  );

  const uiConfig: BettingInsightsUIConfig<PositiveEvIndex, any> = {
    card: {
      styles,
      table: {
        body: {
          render(_context, { bookmakers, content, label, resultIndex, styles, table }) {
            return (
              <PositiveEv.Body
                bookmakers={bookmakers}
                content={content}
                label={label}
                resultIndex={resultIndex}
                styles={styles}
                table={table}
              />
            );
          },
        },
        footer: {
          render(context, { content }) {
            const isSoccerEvTrendsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__SOCCER_EV_TRENDS)?.enabled ??
              false;
            if (content.categoryGroupName === 'FOOTBALL' && !isSoccerEvTrendsEnabled) {
              return null;
            }

            const formatDateToTimezone = (date: string, format?: string) => {
              return formatDate(date, format, context.repubConfig);
            };

            const repub = context.repubConfig.name;
            const repubCountry = isOcPlusRepub(repub) ? REPUB_COUNTRY[repub] : REPUB_COUNTRY.OC;

            return (
              <PositiveEv.Footer
                content={content}
                formatDate={formatDateToTimezone}
                repubCountry={repubCountry}
                repubIs={factoryRepubIs}
              />
            );
          },
        },
        getContent(_context, { result }) {
          return [result];
        },
        headings: {
          row: 3,
          content: [
            {
              key: 'aiProbability',
              render() {
                return evTranslations.aiProbability;
              },
              renderMobile() {
                return evTranslations.aiProbabilityShort;
              },
              tooltipText: evTranslations.aiProbabilityTooltip,
            },
            {
              key: 'sportsbookProbability',
              render() {
                return evTranslations.impliedProbability;
              },
              renderMobile() {
                return evTranslations.impliedProbabilityShort;
              },
              tooltipText: evTranslations.impliedProbabilityTooltip,
              className: 'sportsPorbabilityHeading',
            },
            {
              key: 'expectedValue',
              render() {
                return evTranslations.expectedValue;
              },
              tooltipPosition: 'left',
              tooltipText: evTranslations.expectedValueTooltip,
              highlight: true,
              justifyContent: 'center',
            },
            {
              key: 'bestOdds',
              render(context) {
                const repub = context.repubConfig.name;

                if (repub === REPUBS.WS.name) {
                  return translations.odds;
                }

                return translations.bestOdds;
              },
              mobileView: 'hide',
              justifyContent: 'center',
            },
          ],
        },
        main: {
          render(context, { content, resourcesUrl }) {
            const repub = context.repubConfig.name;
            if (!isOcPlusRepub(repub)) {
              return null;
            }

            const q4PropsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__Q4_PROPS)?.enabled ?? false;

            const repubCountry = REPUB_COUNTRY[repub];
            const betName = content.betNameMap?.[repubCountry] ?? content.betName;
            const lineName = content.line?.nameMap?.[repubCountry] ?? content.line?.name;
            return (
              <Profile
                betName={betName}
                line={content.line}
                lineName={lineName}
                marketClassification={content.marketClassification}
                marketDisplayName={content.marketDisplayNameMap[repubCountry] ?? content.marketDisplayNameMap.us}
                marketPropType={content.marketPropType}
                resourcesUrl={resourcesUrl}
                sportsData={content.sportsData}
                teamData={content.teamData}
                {...(q4PropsEnabled && { type: content.type })}
              />
            );
          },
        },
        cells: [
          {
            key: 'aiProbability',
            render(_context, { content, resultIndex: _ }) {
              return <Progress aria-label="AI Probability" value={Math.round(content.aiProbability * 100) / 100} />;
            },
            alignItems: 'flex-end',
          },
          {
            key: 'sportsbookProbability',
            render(_context, { content }) {
              return (
                <Progress
                  aria-label="Implied Probability"
                  value={Math.round(content.sportsbookProbability * 100) / 100}
                />
              );
            },
            alignItems: 'flex-end',
          },
          {
            key: 'expectedValue',
            render(_context, { content }) {
              return <>{content.expectedValue}%</>;
            },
            highlight: ({ content }) => content.expectedValueStatus?.toLowerCase(),
            justifyContent: 'center',
          },
          {
            key: 'bestOdds',
            render(context, { bookmakers, content }) {
              const repub = context.repubConfig.name;
              const bookmaker = bookmakers[content.bookmakerCode];
              if (!bookmaker) {
                return null;
              }
              const hasMinBookmakers = hasMinBookmakersForRepub(repub, content.bookmakersCount);

              const onClick = memoize(() => {
                trackEvBetClick({ bookmaker, component: 'Positive EV', result: content });
              });

              const isUkOrIe = geolocationInfo?.countryCode === 'GB';

              const isBetDisabled = (repub === REPUBS.WS.name && !isUkOrIe) || !hasMinBookmakers;

              const hideBookieLogo = repub === REPUBS.WS.name || repub === REPUBS.GZ.name || isBetDisabled;

              return (
                <BetButton
                  aria-label={`Place a bet on ${content.betName} ${
                    content.marketDisplayNameMap[repub.toLowerCase() as keyof MarketDisplayNames]
                  }
                      with ${bookmaker.bookmakerName} at odds of ${content.oddsAmerican}`}
                  bet={content}
                  bookieColour={bookmaker.brandColour || 'black'}
                  bookieLogo={bookmaker.logoSmall}
                  bookieName={bookmaker.bookmakerName}
                  disabled={isBetDisabled}
                  hideBookieLogo={hideBookieLogo}
                  onClick={onClick}
                  shouldHideLabelOnDesktop
                  unformattedOdds={content}
                />
              );
            },
            isFullWidthMobile: true,
            justifyContent: 'center',
          },
        ],
      },
      title: {
        format:
          usageContext === 'match-centre-tab'
            ? ''
            : ({ result }) => {
                const { categoryGroupName, subeventName, teamData } = result;
                const { matchupString } = getMatchupTeamInfo({ categoryGroupName, subeventName, teamData });
                const countryCode =
                  requestContext.repubConfig.countryCode.toLocaleLowerCase() as keyof MarketDisplayNames;
                const eventDisplayName = result.eventDisplayNameMap?.[countryCode] || '{eventDisplayName}';
                return `${eventDisplayName} | ${matchupString} | {subeventStartTime}`;
              },
      },
      wrapper(_context, { children, config, content, result }) {
        return (
          <PositiveEv config={config} content={content} result={result}>
            {children}
          </PositiveEv>
        );
      },
    },
    initialUiState(context) {
      const plusConfig = REPUB_CONFIG[context.repubConfig.name]?.plus;
      return {
        hitsPerPage: 10,
        range: {
          aiProbability: context.repubConfig.name === REPUBS.US.name ? '30:95' : '30:80',
        },
        refinementList: {
          eventDisplayName: [plusConfig?.valueBets.defaultEventDisplayName ?? 'EPL'],
        },
        sortBy: plusConfig?.valueBets.defaultSortBy ?? 'search-ev-bets_sportsbookProbability_desc',
      };
    },
    refinements: {
      clearAll: {
        excludedAttributes: [],
      },
      components: [
        {
          attribute: 'bookmakerCode',
          getLabel(_context) {
            return 'Bookmaker (Internal use only)';
          },
          render(context, { bookmakers }) {
            const areInternalSearchToolsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS)
                ?.enabled ?? false;

            if (!areInternalSearchToolsEnabled) {
              return null;
            }

            return (
              <CheckboxFilter
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Bookmaker (Internal use only)"
                repubCountry={factoryRepubCountry}
                sortBy={['name:asc']}
                transformFieldLabel={({ count, label }) => {
                  const bookmaker = bookmakers[label];
                  return `${bookmaker?.bookmakerName ?? label} (${count})`;
                }}
              />
            );
          },
        },
        {
          attribute: 'expectedValue',
          getLabel(_context) {
            return translations.minimumEV;
          },
          render(context) {
            return (
              <SliderFilter
                attribute={context.attribute}
                data-analytics="minimumEv"
                isDisabled={context.isWalled}
                label={translations.minimumEV}
                max={200}
              />
            );
          },
        },
        {
          attribute: 'aiProbability',
          getLabel(_context) {
            return translations.minimumAIProb;
          },
          render(context, { currentRefinements }) {
            const eventDisplayName = currentRefinements.find(refinement => refinement.attribute === 'eventDisplayName')
              ?.refinements[0].value;

            const repubEventDisplayNames = getEventDisplayNames(context.repubConfig.name);

            const isSoccer =
              repubEventDisplayNames[eventDisplayName as keyof typeof repubEventDisplayNames]?.categoryGroupName ===
              FOOTBALL;

            return (
              <SliderFilter
                attribute={context.attribute}
                data-analytics="minimumAi"
                isDisabled={context.isWalled}
                label={translations.minimumAIProb}
                max={context.repubConfig.name === REPUBS.OC.name || isSoccer ? 80 : 95}
                min={1}
              />
            );
          },
        },
        {
          attribute: 'sportsData.hitRates.last10',
          getLabel(_context) {
            return translations.minimumHitRate;
          },
          render(context) {
            return (
              <SliderFilter
                attribute={context.attribute}
                data-analytics="minimumHitRate"
                isDisabled={context.isWalled}
                label={translations.minimumHitRate}
                max={100}
                step={10}
              />
            );
          },
        },
        {
          // Use subeventStartDateLocal for GZ (local timezone), subeventStartDate for others (UTC)
          attribute: dateAttribute as 'subeventStartDate' | 'subeventStartDateLocal',
          getLabel(_context) {
            return translations.date;
          },
          render(context) {
            const isMatchCentreTab = usageContext === 'match-centre-tab';
            if (isMatchCentreTab) return null;

            return (
              <DateRefinement
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Dates"
                repubCountry={factoryRepubCountry}
              />
            );
          },
        },
        {
          attribute: 'marketPropType',
          getLabel() {
            return translations.propsFilter;
          },
          render(context) {
            const q4PropsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__Q4_PROPS)?.enabled ?? false;

            const label = translations.propsFilter;

            return (
              <CheckboxFilter
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label={label}
                repubCountry={factoryRepubCountry}
                sortBy={['name:asc']}
                transformFieldLabel={({ label: fieldLabel }) =>
                  requestContext.repubConfig.name === 'GZ'
                    ? fieldLabel.replace('Player Props', translations.player).replace('Team Props', translations.team)
                    : fieldLabel.replace('Props', translations.propsFilter)
                }
                {...(q4PropsEnabled && { childrenPlacement: 'before' })}
              >
                {q4PropsEnabled && (
                  <CheckboxFilter
                    attribute="type"
                    isDisabled={context.isWalled}
                    repubCountry={factoryRepubCountry}
                    showMore={false}
                    transformFieldLabel={({ label }) => <BetTypeLabel size="small" type={label} />}
                  />
                )}
              </CheckboxFilter>
            );
          },
        },
      ],
    },
    results: {
      categories(context) {
        const isMatchCentreTab = usageContext === 'match-centre-tab';
        if (isMatchCentreTab) return null;

        const repubName = context.repubConfig.name;
        const categoryOrdering =
          CATEGORY_ORDERING_CONFIGS[repubName as keyof typeof CATEGORY_ORDERING_CONFIGS] ??
          CATEGORY_ORDERING_CONFIGS.OC;

        return {
          attribute: EVENT_DISPLAY_NAME,
          labels: getEventDisplayNames(repubName),
          order: categoryOrdering,
        };
      },
      labelledBy: '#bet-name',
      propsName: 'bets',
    },
    search: {
      render(context, { searchAttributes }) {
        const textSearchEnabled =
          context.featureFlags.find(f => f.name === featureFlags.SUBS__PLUS__TEXT_SEARCH)?.enabled ?? false;

        if (!textSearchEnabled) {
          return null;
        }

        return (
          <Autocomplete
            disabled={context.isWalled}
            indexName={positiveEvIndex}
            repub={context.repubConfig.name}
            searchAttributes={searchAttributes}
          />
        );
      },
    },
    sorting: {
      canSort() {
        return true;
      },
    },
  };

  return { ui: createBettingInsightsUIConfig(requestContext, uiConfig, tags) };
}
